import { usePostHog } from '@posthog/react';
import { Button } from '../Button';
import { CATALOG_API_COUNT } from '../../lib/catalogSize';
import { HUMAN_SIGNUP_OFFER_CTA, STARTER_BALANCE } from '../../lib/signupOffer';

/**
 * Decorative geometry echoing the OG cards: flat blocks, a circle, and a
 * quarter-round, in the two brand tones over the accent field. Purely visual and
 * hidden from assistive tech; it is dropped below `sm`, where the card is all
 * text and the shapes would only crowd the copy.
 */
const Geometry = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 hidden w-48 sm:block">
    <div className="absolute right-0 top-0 h-16 w-16 bg-white/10" />
    <div className="absolute right-16 top-0 h-16 w-16 rounded-bl-full bg-white/[0.07]" />
    <div className="absolute right-6 top-20 size-12 rounded-full bg-white/[0.13]" />
    <div className="absolute right-24 bottom-4 h-20 w-20 rounded-tr-[40px] bg-white/[0.06]" />
    <div className="absolute right-0 bottom-0 h-10 w-28 bg-white/[0.09]" />
  </div>
);

/**
 * The value card that sits under every post's summary.
 *
 * Blog was the clearest hole the Win-stage analysis found: over the 90 days to
 * 2026-08-17, 128 sessions entered a post and zero of them signed up, and none
 * of the 35 lifetime payers first landed on one. The posts already funnel to
 * `/catalog` further down through the plain `CTA` block; this is the earlier,
 * louder ask - what AnyAPI is and what it costs, at the point a reader has just
 * read the summary and knows whether the topic is theirs.
 *
 * It is deliberately on-brand rather than a plain box: the accent field and flat
 * geometry are the OG cards' visual language, so it reads as the product
 * speaking, not as another aside in the prose.
 */
export const BlogValueCta = () => {
  const posthog = usePostHog();

  return (
    <div className="not-prose relative my-6 overflow-hidden rounded-12 bg-accent px-5 py-5 text-white sm:px-6">
      <Geometry />
      <div className="relative max-w-[42ch]">
        <p className="mono text-11 uppercase tracking-[0.08em] text-white/70">Built on AnyAPI</p>
        <p className="mt-2 text-18 font-semibold leading-snug tracking-[-0.015em]">
          {CATALOG_API_COUNT} scraping and data APIs behind one key.
        </p>
        <p className="mt-1.5 text-14 leading-relaxed text-white/85">
          Every endpoint in this post is one call away. Pay per request in USD from a prepaid wallet, with automatic
          failover when a provider breaks. No subscription, no card to start.
        </p>
        <Button
          as="a"
          to="/sign-up"
          variant="line"
          className="mt-4 border-white/30 bg-white text-accent hover:bg-white/90"
          onClick={() => posthog?.capture('blog_value_cta_clicked')}
        >
          {HUMAN_SIGNUP_OFFER_CTA}
        </Button>
        <span className="mt-2 block text-12 text-white/70">
          {STARTER_BALANCE} of credit on signup.
        </span>
      </div>
    </div>
  );
};
